import viewer from "../../workers/ViewerEngine";
import * as Enumerations from "../../../Enumerations/Enumerations";
import * as Interfaces from "../../../Interfaces/Interfaces";
import * as BABYLON from 'babylonjs';
import * as Data from "../../Data/WebData";
import EventManager from '../../Managers/EventManager';
import Viewer3DConstants from '../../../constants/Viewer3DConstants';
import Viewer3DActions from '../../../actions/Viewer3DActions';
import Viewer3DEvents from '../../../constants/Viewer3DEvents';
import Viewer from './Viewer';
import { Octree } from "babylonjs/Culling/Octrees/octree";
import { ContentLayers } from "../../../Interfaces/ViewerInterfaces";
import { QualityConfig } from "../../Data/WebData";
import * as Inovx from '@shared';
import { Quaternion } from "babylonjs";
import { planRetainedAssetSetRootUpdate, resolveFallbackAssetLayerID } from '../../../helpers/viewerAssetSetModel';

var SystemActions = Inovx.Shared.System.Actions;

const THRESHOLD = 1e300; // treat absurd magnitudes as invalid
const SENTINEL_APPROX = 1.7976931348623156e305; // value you observed
const EPS = SENTINEL_APPROX * Number.EPSILON * 8;

interface IResolvedOntologyBounds {
    source: "map" | "content" | "none";
    ontologyId?: string;
    minBounds?: IVector2D;
    maxBounds?: IVector2D;
}

class Viewer3D extends Viewer {

    private _lastOctreeRequest: {
        ontologyId: string;
        projectId: string;
        selectedOntologyId?: string;
        mapBoundsSig?: string;
        rangeViewPosition?: IVector3D;
        distanceThreshold?: number;
        camPosition?: IVector3D;
        camDirection?: IVector3D;
        camYaw?: number;
        camPitch?: number;
        camFocalDistance?: number;
        repositionCamera?: boolean;
        assetSetIds?: string[];
        assetSetIsolation?: boolean;
    } | null = null;

    private _pendingSetupRequest: {
        ontologyId: string;
        projectId: string;
        ontologyBoundsContext?: IOntologyBoundsContext;
        rangePosition?: IVector3D;
        rangeDistance?: number;
        assetSets?: IAssetSet[];
        repositionCamera?: boolean;
        cameraPosition?: IVector3D;
        cameraDirection?: IVector3D;
        focalDistance?: number;
    } | null = null;

    private _lastColorizeSig: string | null = null;
    private _cachedBaseOctreeData: any[] = [];
    private _cachedMinDisplayBounds: IVector2D | null = null;
    private _cachedMaxDisplayBounds: IVector2D | null = null;
    private _cachedOctreeProjectId: string | null = null;
    private _assetSetOctreeRootCache = new Map<string, { versionToken: string; root: any }>();
    private _registeredAssetSetRootVersions = new Map<string, string>();
    private _currentFallbackAssetLayerID: string | null = null;
    private _assetSetOctreeGeneration = 0;
    private _appearanceData: Interfaces.Interfaces.IAppearanceObject[];
    private _annotationCanvas2d: HTMLCanvasElement;
    private _drawCanvas2d: HTMLCanvasElement;
    private _graphicsBaseUrl: string;
    private _pickMode: Enumerations.PickMode;
    private _isFocusing: boolean;
    private _isZoomArea: boolean;
    private _lightingMode: Enumerations.LightingMode = "ambient";
    private _totalAssetCount: number;
    private _currentAssetCount: number;
    private _lodData: string;
    private _lodOptions: string;
    private _initializedOctree: boolean = false;
    private _defaultCameraSettings: Interfaces.Interfaces.ICameraSettings = {
        CameraType: 1,
        CameraPosition: { X: 0, Y: 0, Z: 0 },
        CameraRotation: { X: 1, Y: 0, Z: 0, W: 0 },
        CameraHeight: 0,
        CameraFocalDistance: 0,
        RangePosition: null,
        RangeDistance: null
    };

    // NEW: generation counter to prevent stale async octree work from winning
    private _octreeGeneration: number = 0;

    constructor(div: HTMLDivElement, graphicsBaseUrl: string, lodData: string, lodOptions: string) {

        super(div);
        this._bindFunctions();

        this._graphicsBaseUrl = graphicsBaseUrl;

        this._appearanceData = [];
        this._totalAssetCount = 0;
        this._currentAssetCount = 0;
        this._lodData = lodData;
        this._lodOptions = lodOptions;
        this._initialize();

        this._pickMode = Enumerations.PickMode.SELECT;
        this._isFocusing = false;
        this._isZoomArea = false;
    }

    private _bindFunctions() {
        //private
        this._loadMaterials = this._loadMaterials.bind(this);
        this._requestGraphics = this._requestGraphics.bind(this);
        this._beginGraphicDisplay = this._beginGraphicDisplay.bind(this);
        this._requestAppearances = this._requestAppearances.bind(this);
        this._resizeCanvas = this._resizeCanvas.bind(this);
        this._addEventListeners = this._addEventListeners.bind(this);
        this._removeEventListeners = this._removeEventListeners.bind(this);
        this._registerEvents = this._registerEvents.bind(this);
        this._focuseFinished = this._focuseFinished.bind(this);
        this._setDisplayMode = this._setDisplayMode.bind(this);
        this._setCameraMode = this._setCameraMode.bind(this);
        this._zoomAreaFinished = this._zoomAreaFinished.bind(this);
        this._displayCountUpdate = this._displayCountUpdate.bind(this);
        this._displayMemoryLimit = this._displayMemoryLimit.bind(this);
        this._fireEndBlockingOperation = this._fireEndBlockingOperation.bind(this);
        this._fireStartBlockingOperation = this._fireStartBlockingOperation.bind(this);

        //public
        this.dispose = this.dispose.bind(this);
        this.requestData = this.requestData.bind(this);
        this.requestGraphicAppearance = this.requestGraphicAppearance.bind(this);
        this.registerEvent = this.registerEvent.bind(this);
        this.zoomAll = this.zoomAll.bind(this);
        this.zoomIn = this.zoomIn.bind(this);
        this.zoomOut = this.zoomOut.bind(this);
        this.zoomArea = this.zoomArea.bind(this);
        this.stopZoomArea = this.stopZoomArea.bind(this);
        this.zoomToObjects = this.zoomToObjects.bind(this);
        this.zoomToMeasurements = this.zoomToMeasurements.bind(this);
        this.zoomToScan = this.zoomToScan.bind(this);
        this.alignToScan = this.alignToScan.bind(this);
        this.allowPick = this.allowPick.bind(this);
        this.zoomToExtent = this.zoomToExtent.bind(this);
        this.highlightObjects = this.highlightObjects.bind(this);
        this.unhighlightObjects = this.unhighlightObjects.bind(this);
        this.hideObjects = this.hideObjects.bind(this);
        this.unhideObjects = this.unhideObjects.bind(this);
        this.highlightSingleObject = this.highlightSingleObject.bind(this);
        this.unhighlightAllObjects = this.unhighlightAllObjects.bind(this);
        this.getOverrideMaterialOfAsset = this.getOverrideMaterialOfAsset.bind(this);
        this.getMeshKeysOfAsset = this.getMeshKeysOfAsset.bind(this);
        this.viewUp = this.viewUp.bind(this);
        this.viewDown = this.viewDown.bind(this);
        this.viewNorth = this.viewNorth.bind(this);
        this.viewSouth = this.viewSouth.bind(this);
        this.viewEast = this.viewEast.bind(this);
        this.viewWest = this.viewWest.bind(this);
        this.changeAssetsMaterial = this.changeAssetsMaterial.bind(this);
        this.changeMaterialProperties = this.changeMaterialProperties.bind(this);
        this.overrideSelectionMaterial = this.overrideSelectionMaterial.bind(this);
        this.focusAsset = this.focusAsset.bind(this);
        this.stopFocus = this.stopFocus.bind(this);
        this.setNavMode = this.setNavMode.bind(this);
        this.setPanMode = this.setPanMode.bind(this);
        this.setUniversalMode = this.setUniversalMode.bind(this);
        this.setFlyMode = this.setFlyMode.bind(this);
        this.setMultiSelectEnabled = this.setMultiSelectEnabled.bind(this);
        this.setCrosshairEnabled = this.setCrosshairEnabled.bind(this);
        this.setCameraInvertY = this.setCameraInvertY.bind(this);
        this.setCameraRotationSensitivity = this.setCameraRotationSensitivity.bind(this);
        this.setCameraVelocitySensitivity = this.setCameraVelocitySensitivity.bind(this);
        this.exitFollowCamera = this.exitFollowCamera.bind(this);
        this.setWalkMode = this.setWalkMode.bind(this);
        this.setMeasurementMode = this.setMeasurementMode.bind(this);
        this.setViewerClickMode = this.setViewerClickMode.bind(this);
        this.setBackgroundScene = this.setBackgroundScene.bind(this);
        this.setCameraFocalDistance = this.setCameraFocalDistance.bind(this);
        this.setCameraPosition = this.setCameraPosition.bind(this);
        this.initializeCameraProperties = this.initializeCameraProperties.bind(this);
        this.setDisplayModeWireframe = this.setDisplayModeWireframe.bind(this);
        this.setDisplayModeSolid = this.setDisplayModeSolid.bind(this);
        this.setPerspectiveCamera = this.setPerspectiveCamera.bind(this);
        this.setOrthographicCamera = this.setOrthographicCamera.bind(this);
        this.clearMeasurements = this.clearMeasurements.bind(this);
        this.displayObjects = this.displayObjects.bind(this);
        this.changeCameraFocalDistance = this.changeCameraFocalDistance.bind(this);
        this.getCanvasSize = this.getCanvasSize.bind(this);
        this.getScreenshot = this.getScreenshot.bind(this);
        this.setMeshShadeType = this.setMeshShadeType.bind(this);
        this.setPointShadeType = this.setPointShadeType.bind(this);
        this.setFollowNavMode = this.setFollowNavMode.bind(this);
    }

    private _initialize() {
        this._createViewer();
        this._registerEvents();
        this._resizeCanvas();
    }

    protected _resizeCanvas() {
        var devicePixelRatio = window.devicePixelRatio || 1;
        var padding = 15;

        this._fillStyle == Enumerations.ViewerFillStyle.FILL ? this._canvas.parentElement.parentElement.style.padding = "0px" : this._canvas.parentElement.parentElement.style.padding = "15px";
        var containerWidth = this._canvas.parentElement.clientWidth;
        var containerHeight = this._canvas.parentElement.clientHeight;
        var width = containerWidth;
        var height = containerHeight;

        if (this._fillStyle == Enumerations.ViewerFillStyle.FILL) {

            width = containerWidth;
            height = containerHeight;
        }
        else if (this._canvasSize && this._canvasSize.height && this._canvasSize.width) {

            var canvasWidth = this._canvasSize.width;
            var canvasHeight = this._canvasSize.height;
            if (containerWidth / containerHeight > canvasWidth / canvasHeight) {
                // Scale based on Height
                width = Math.ceil(((canvasWidth * containerHeight) / canvasHeight));
            } else {
                height = Math.ceil(((canvasHeight * containerWidth) / canvasWidth));
            }
        }

        if ((this._canvas.width != width || this._canvas.height != height)) {

            this._canvas.style.width = width + 'px';
            this._canvas.style.height = height + 'px';

            this._annotationCanvas2d.style.width = width + 'px';
            this._annotationCanvas2d.style.height = height + 'px';

            this._drawCanvas2d.style.width = width + 'px';
            this._drawCanvas2d.style.height = height + 'px';

            this._viewerEngine.sceneManager.resize();

            this._annotationCanvas2d.width = width;
            this._annotationCanvas2d.height = height;

            this._drawCanvas2d.width = width;
            this._drawCanvas2d.height = height;

            this._eventManager.fireEvent(Viewer3DEvents.VIEWER_RESIZED as any, null);
        }
    }

    private _createViewer() {
        var canvas3dContainer = document.createElement('div');
        canvas3dContainer.className = 'vt-3d-container';
        canvas3dContainer.style.width = '100%';
        canvas3dContainer.style.height = '100%';
        canvas3dContainer.style.display = 'flex';
        canvas3dContainer.style.cursor = 'default';
        canvas3dContainer.style.justifyContent = 'center';

        var height = canvas3dContainer.clientHeight;
        var width = canvas3dContainer.clientWidth;

        // Set 3d canvas styles
        var canvas3d = this._canvas = document.createElement('canvas');
        canvas3d.width = width;
        canvas3d.height = height;
        canvas3d.style.padding = '0';
        canvas3d.style.margin = '0';
        canvas3d.style.backgroundColor = 'transparent';
        canvas3d.style.cursor = 'default';
        canvas3d.style.alignSelf = 'center';
        canvas3d.style.outline = '2px solid black';

        canvas3dContainer.appendChild(canvas3d);

        this._divContainer.appendChild(canvas3dContainer);

        var canvas2dContainer = document.createElement('div');
        canvas2dContainer.className = 'vt-2d-container';
        canvas2dContainer.style.zIndex = '5';
        canvas2dContainer.style.left = '0';
        canvas2dContainer.style.top = '0';
        canvas2dContainer.style.pointerEvents = 'none';
        canvas2dContainer.style.position = 'absolute';
        canvas2dContainer.style.cursor = 'crosshair';
        canvas2dContainer.style.display = 'flex';
        canvas2dContainer.style.width = '100%';
        canvas2dContainer.style.height = '100%';
        canvas2dContainer.style.justifyContent = 'center';

        var canvas2dContentContainer = document.createElement('div');
        canvas2dContentContainer.style.position = 'relative';
        canvas2dContentContainer.style.alignSelf = 'center';
        canvas2dContainer.appendChild(canvas2dContentContainer);

        var annotationCanvas2d = this._annotationCanvas2d = document.createElement('canvas');
        annotationCanvas2d.width = width;
        annotationCanvas2d.height = height;
        annotationCanvas2d.style.cursor = 'crosshair';

        var drawCanvas2d = this._drawCanvas2d = document.createElement('canvas');
        drawCanvas2d.width = width;
        drawCanvas2d.height = height;
        drawCanvas2d.style.zIndex = '10';
        drawCanvas2d.style.left = '0';
        drawCanvas2d.style.top = '0';
        drawCanvas2d.style.position = 'absolute';
        drawCanvas2d.style.cursor = 'crosshair';

        canvas2dContentContainer.appendChild(annotationCanvas2d);
        canvas2dContentContainer.appendChild(drawCanvas2d);

        this._divContainer.appendChild(canvas2dContainer);

        this._viewerEngine = new viewer(canvas3d, annotationCanvas2d, drawCanvas2d);

        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.CAMERABOXZOOMED, this._zoomAreaFinished);

        this._viewerEngine.sceneManager.setPickMode(Enumerations.PickMode.SELECT);
        this._viewerEngine.sceneManager.setSelectionMode(Enumerations.SelectMode.SINGLE);
        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Examine);

        // Babylon is setting touch actions on the style. Therefore, once the engine is initialized, we void out any actions for touch. This means the controls
        // themselves must subscribe and pay attention to the events.
        canvas3d.style.touchAction = 'none';
        canvas2dContainer.style.touchAction = 'none';
        drawCanvas2d.style.touchAction = 'none';
        annotationCanvas2d.style.touchAction = 'none';

        this._addEventListeners();
    }

    private _clearViewer() {
        this._divContainer.innerHTML = '';
    }

    private _loadMaterials() {
        var that = this;
        var materials = new Data.MaterialData.MaterialResults(this._appearanceData);
        var materialLength = materials.DataLength;

        for (var i = 0; i < materialLength; i++) {
            materials.GetMaterialType(i) == 1 ? this._viewerEngine.materialManager.addMarkedPointMaterial(materials.MarkedPointMaterialResult(i)) :
                this._viewerEngine.materialManager.addMaterial(materials.MaterialResult(i));
        }
    }


    private _requestGraphics(assetObjectListId: string, projectId: string, ontologyBoundsContext?: IOntologyBoundsContext, camPosition?: IVector3D, camPitch?: number, camYaw?: number, rangeViewPosition?: IVector3D, distanceThreshold?: number, overrideFocalDistance?:number, assetSets?: IAssetSet[], assetSetIsolation?: boolean, endCb?: () => void) {
        var that = this;
        this._loadMaterials();

        that._beginGraphicDisplay(
            assetObjectListId,
            projectId,
            function () {

                that._eventManager.fireEvent(Viewer3DEvents.DISPLAY_STARTED as any, null);
            },
            ontologyBoundsContext,
            camPosition,
            camPitch,
            camYaw,
            rangeViewPosition,
            distanceThreshold,
            overrideFocalDistance,
            assetSets,
            assetSetIsolation,
            endCb
        );
    }

    private _buildOctreeContentBoundsMap(octreeRoots: any[]): IOntologyContentBounds {
        const map: IOntologyContentBounds = {};

        for (const root of octreeRoots || []) {
            const ontologyId = root?.ontologyId;
            const extents = root?.Extents;

            if (!ontologyId || !this._isValidOctreeExtents(extents)) continue;

            const min = extents.Minimum;
            const max = extents.Maximum;

            map[ontologyId] = {
                min: {
                    X: min.X / 1000,
                    Y: min.Z / 1000,
                    Z: min.Y / 1000
                },
                max: {
                    X: max.X / 1000,
                    Y: max.Z / 1000,
                    Z: max.Y / 1000
                }
            };
        }

        return map;
    }

    private _intersectBounds2D(
        aMin?: IVector2D,
        aMax?: IVector2D,
        bMin?: IVector2D,
        bMax?: IVector2D
    ): { minBounds?: IVector2D; maxBounds?: IVector2D } {
        if (!aMin || !aMax) {
            return { minBounds: bMin, maxBounds: bMax };
        }
        if (!bMin || !bMax) {
            return { minBounds: aMin, maxBounds: aMax };
        }

        const minX = Math.max(aMin.X, bMin.X);
        const minY = Math.max(aMin.Y, bMin.Y);
        const maxX = Math.min(aMax.X, bMax.X);
        const maxY = Math.min(aMax.Y, bMax.Y);

        if (minX > maxX || minY > maxY) {
            return {
                minBounds: aMin,
                maxBounds: aMax
            };
        }

        return {
            minBounds: { X: minX, Y: minY },
            maxBounds: { X: maxX, Y: maxY }
        };
    }

    private _isValidOctreeExtents(extents: any): boolean {
        const min = extents?.Minimum;
        const max = extents?.Maximum;

        if (!min || !max) return false;

        const values = [
            min.X, min.Y, min.Z,
            max.X, max.Y, max.Z
        ];

        for (const value of values) {
            if (!Number.isFinite(value)) return false;
        }

        if (min.X > max.X || min.Y > max.Y || min.Z > max.Z) return false;

        return true;
    }

    private _resolveOntologyBounds(
        context: IOntologyBoundsContext | undefined,
        octreeContentBoundsByOntologyId: IOntologyContentBounds
    ): IResolvedOntologyBounds {
        if (!context?.selectedOntologyId) {
            return { source: "none" };
        }

        const selectedId = context.selectedOntologyId;
        const mapBounds = context.mapBoundsByOntologyId || {};
        const hierarchy = context.ontologyHierarchy || {};

        let currentId: string | null | undefined = selectedId;
        const visited: { [id: string]: boolean } = {};

        while (currentId && !visited[currentId]) {
            visited[currentId] = true;

            const map = mapBounds[currentId];
            if (map?.min && map?.max) {
                return {
                    source: "map",
                    ontologyId: currentId,
                    minBounds: {
                        X: Math.min(map.min.X, map.max.X),
                        Y: Math.min(map.min.Y, map.max.Y)
                    },
                    maxBounds: {
                        X: Math.max(map.min.X, map.max.X),
                        Y: Math.max(map.min.Y, map.max.Y)
                    }
                };
            }

            const content = octreeContentBoundsByOntologyId[currentId];
            if (content?.min && content?.max) {
                return {
                    source: "content",
                    ontologyId: currentId,
                    minBounds: { X: content.min.X, Y: content.min.Z },
                    maxBounds: { X: content.max.X, Y: content.max.Z }
                };
            }

            currentId = hierarchy[currentId]?.parentId ?? null;
        }

        return { source: "none" };
    }

    private _sigMapBounds(context?: IOntologyBoundsContext): string {
        const selectedOntologyId = context?.selectedOntologyId ?? "";
        const hierarchy = context?.ontologyHierarchy ?? {};
        const mapBounds = context?.mapBoundsByOntologyId ?? {};
        const visited: { [id: string]: boolean } = {};
        const parts: string[] = [];

        let currentId: string | null | undefined = selectedOntologyId;

        while (currentId && !visited[currentId]) {
            visited[currentId] = true;
            const bounds = mapBounds[currentId];
            const boundsSig = bounds?.min && bounds?.max
                ? `${bounds.min.X},${bounds.min.Y},${bounds.max.X},${bounds.max.Y}`
                : "";

            parts.push(`${currentId}|${boundsSig}`);
            currentId = hierarchy[currentId]?.parentId ?? null;
        }

        return `${selectedOntologyId}::${parts.join("||")}`;
    }

    private _getGraphicDataPageSize(graphicIndex: number): number {

        if (graphicIndex <= 400) {
            return 400;
        }
        else if (graphicIndex <= 800) {
            return 800;
        }
        else if (graphicIndex <= 1200) {
            return 1200;
        }
        else {
            return 2000;
        }
    }

    private _getDisplayBatchSize(): number {
        return 200;
    }

    private _beginGraphicDisplay(assetObjectListId: string, projectId: string, beginCb: () => void, ontologyBoundsContext?: IOntologyBoundsContext, camPosition?: IVector3D, camPitch?: number, camYaw?: number, rangePosition?: IVector3D, distanceThreshold?: number, overrideFocalDistance?:number, assetSets?: IAssetSet[], assetSetIsolation?: boolean, endCb?: () => void) {

        beginCb();

        this._requestGraphicData(assetObjectListId, projectId, assetSets || [], assetSetIsolation || false, ontologyBoundsContext, camPosition, camPitch, camYaw, rangePosition, distanceThreshold, overrideFocalDistance, endCb);
    }

    private static _qualityCfgCache: QualityConfig | null = null;

    private async _loadQualityConfig(): Promise<QualityConfig> {

        if (Viewer3D._qualityCfgCache) return Viewer3D._qualityCfgCache;
        const base = Viewer3DActions.getLODGraphicsUrl();
        const url = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + "/lod/pointcloud-quality.json";
        const r = await fetch(url, { cache: "no-cache" });
        if (!r.ok) throw new Error("Failed to load pointcloud-quality.json: " + r.status);
        const cfg = (await r.json()) as QualityConfig;
        Viewer3D._qualityCfgCache = cfg;
        return cfg;
    }

    private async _setupOctreeData(
        ontologyId: string,
        projectId: string,
        assetSets: IAssetSet[] | undefined,
        options: {
            ontologyBoundsContext?: IOntologyBoundsContext;
            camPosition?: IVector3D;
            camDirection?: IVector3D;
            camPitch?: number;
            camYaw?: number;
            camFocalDistance?: number;
            rangeViewPosition?: IVector3D;
            distanceThreshold?: number;
            generation?: number;           // you can keep this, but you won’t need it anymore
            repositionCamera?: boolean;
            assetSetIsolation?: boolean;
        }
    ): Promise<void> {

        assetSets = assetSets ?? [];

        let {
            camPosition,
            camDirection,
            camPitch,
            camYaw,
            rangeViewPosition,
            distanceThreshold,
            repositionCamera = true,
            assetSetIsolation = false
        } = options;

        const normalizedAssetSetIds = this._normalizeAssetSetIds(assetSets);

        const shouldUpdate = this._shouldRebuildOctree(ontologyId, projectId, {
            ontologyBoundsContext: options.ontologyBoundsContext,
            rangeViewPosition: options.rangeViewPosition,
            distanceThreshold: options.distanceThreshold,
            camPosition: options.camPosition,
            camDirection: options.camDirection,
            camYaw: options.camYaw,
            camPitch: options.camPitch,
            camFocalDistance: options.camFocalDistance,
            repositionCamera: options.repositionCamera,
            assetSetIds: normalizedAssetSetIds,
            assetSetIsolation
        });

        var zoomBounds;

        if (shouldUpdate) {
            const cfg = await this._loadQualityConfig();

            const callerOpts = this._lodOptions ? JSON.parse(this._lodOptions) : {};
            const mergedOptions = {
                ...callerOpts,
                ...cfg.settings,
                QualityConfig: cfg
            };
            const mergedOptionsStr = JSON.stringify(mergedOptions);

            let regionData: any = this._lodData;
            if (typeof regionData === "string") {
                try { regionData = JSON.parse(regionData); } catch { }
            }

            (this._viewerEngine.displayManager as any).clearAllContent();

            const baseOctreeModels = await this._fetchOctree(ontologyId, projectId) as any[];

            const baseOctreeData = (baseOctreeModels || [])
                .map(model => {
                    if (!model?.TreeData) return null;

                    try {
                        const root = JSON.parse(model.TreeData);
                        root.ontologyId = model.OntologyID;
                        return root;
                    } catch (e) {
                        console.warn("Failed to parse octree TreeData for ontology", model?.OntologyID, e);
                        return null;
                    }
                })
                .filter((r): r is any => !!r);

            const octreeContentBoundsByOntologyId = this._buildOctreeContentBoundsMap(baseOctreeData);

            const resolvedOntologyBounds = this._resolveOntologyBounds(
                options.ontologyBoundsContext,
                octreeContentBoundsByOntologyId
            );

            let minDisplayBounds = resolvedOntologyBounds.minBounds;
            let maxDisplayBounds = resolvedOntologyBounds.maxBounds;

            if (rangeViewPosition != null && distanceThreshold != null) {
                const rangeMin: IVector2D = {
                    X: rangeViewPosition.X - distanceThreshold,
                    Y: rangeViewPosition.Z - distanceThreshold
                };
                const rangeMax: IVector2D = {
                    X: rangeViewPosition.X + distanceThreshold,
                    Y: rangeViewPosition.Z + distanceThreshold
                };

                const clampedRange = this._intersectBounds2D(
                    minDisplayBounds,
                    maxDisplayBounds,
                    rangeMin,
                    rangeMax
                );

                minDisplayBounds = clampedRange.minBounds;
                maxDisplayBounds = clampedRange.maxBounds;
            }

            if (minDisplayBounds?.X == 0 && minDisplayBounds?.Y == 0 &&
                maxDisplayBounds?.X == 0 && maxDisplayBounds?.Y == 0) {
                minDisplayBounds = null;
                maxDisplayBounds = null;
            }

            const lodUrl = Viewer3DActions.getLODGraphicsUrl();
            const tileData = regionData
                ? await (this._viewerEngine.displayManager as any).setupRegionOctree(
                    lodUrl,
                    mergedOptionsStr,
                    regionData,
                    minDisplayBounds,
                    maxDisplayBounds
                )
                : null;

            if (this._cachedOctreeProjectId !== projectId) {
                this._assetSetOctreeRootCache.clear();
                this._assetSetOctreeGeneration++;
            }
            // setupViewerData replaces the coordinator, even when the project
            // itself is unchanged (for example, a new ontology/range context).
            this._registeredAssetSetRootVersions.clear();
            this._currentFallbackAssetLayerID = null;
            this._cachedBaseOctreeData = this._cloneOctreeRoots(baseOctreeData);
            this._cachedMinDisplayBounds = minDisplayBounds;
            this._cachedMaxDisplayBounds = maxDisplayBounds;
            this._cachedOctreeProjectId = projectId;

            let assetSetRoots = await this._buildAssetSetRoots(assetSets);

            const octreeData: any[] = [
                ...(baseOctreeData || []),
                ...assetSetRoots
            ];

            const hasRegionContent = tileData != null;

            if (!hasRegionContent && !octreeData.length) {
                return;
            }

            if (hasRegionContent) {
                (this._viewerEngine.sceneManager as any).setRegionCoordinatorOctree(
                    (this._viewerEngine.displayManager as any).processRegionMemoryPressureDisplay,
                    tileData.roots ?? []
                );
            }

            let octreeBoundingBox: BABYLON.BoundingBox | null = null;

            if (octreeData.length) {
                const allAssetSet = assetSets.find(assetSet => assetSet.Type === AssetSetType.All3DAssets);
                const fallbackAssetLayerID = resolveFallbackAssetLayerID(
                    allAssetSet?.ID,
                    assetSetRoots.length > 0,
                    assetSetIsolation
                );
                this._currentFallbackAssetLayerID = fallbackAssetLayerID;

                for (let i = 0; i < octreeData.length; i++) {
                    const node = octreeData[i] as any;
                    const assetSetId = node.AssetSetID || node.assetSetId;
                    node.assetLayerId = assetSetId;

                    if (node.assetLayerId == null && fallbackAssetLayerID) {
                        node.assetLayerId = fallbackAssetLayerID;
                    }
                }

                const setupOctree = (this._viewerEngine.displayManager as any).setupOctree(
                    Viewer3DActions.getOctreeGraphicsUrl(),
                    projectId,
                    [],
                    octreeData,
                    minDisplayBounds,
                    maxDisplayBounds
                );

                this._appearanceData = await this._fetchAppearances(ontologyId);
                this._loadMaterials();

                (this._viewerEngine.sceneManager as any).setCoordinatorOctree(
                    (this._viewerEngine.displayManager as any).processMemoryPressureDisplay,
                    setupOctree
                );

                for (const assetSet of assetSets.filter(assetSet =>
                    assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags)) {
                    this._registeredAssetSetRootVersions.set(
                        assetSet.ID.toLocaleLowerCase(),
                        assetSet.VersionToken ?? ''
                    );
                }

                octreeBoundingBox = this._computeBoundingBox(setupOctree);
            }

            let contentExtents = this._combineContentExtents(
                octreeBoundingBox ?? null,
                tileData?.contentExtents ?? null
            );

            if (contentExtents && this._hasBadBoundingBox(contentExtents)) {
                SystemActions.displayWarningMessage({
                    message: InovxLoader.t('facility:message.noContent3DView'),
                });

                contentExtents = null;
            }

            zoomBounds = this._getZoomBounds(
                contentExtents,
                minDisplayBounds,
                maxDisplayBounds
            );

            const clipBounds = this._getClipBounds(
                contentExtents,
                minDisplayBounds,
                maxDisplayBounds
            );

            (this._viewerEngine.sceneManager as any)._sceneContentExtents = zoomBounds;
            (this._viewerEngine.sceneManager as any).clampSceneToBoundingBox(clipBounds);
        }

        if (!zoomBounds) {
            zoomBounds = (this._viewerEngine.sceneManager as any)._sceneContentExtents ?? null;
        }

    // --- Camera handling differences ---
    if (repositionCamera) {
        
        var top;
        var focalDistance = this._defaultCameraSettings.CameraFocalDistance;
        var q;

        if (camPosition != null && camDirection != null) {
            q = this._quatFromUpToDir(new BABYLON.Vector3(camDirection.X, camDirection.Y, camDirection.Z));
            top = new BABYLON.Vector3(camPosition.X, camPosition.Y, camPosition.Z);

            if (options.camFocalDistance && options.camFocalDistance > 0)
                focalDistance = options.camFocalDistance;

            if (focalDistance == 0)
                focalDistance = 10;

            this._defaultCameraSettings = {
                CameraType: 1,
                CameraPosition: { X: top.x, Y: top.y, Z: top.z },
                CameraRotation: { X: q.x, Y: q.y, Z: q.z, W: q.w },
                CameraHeight: 10,
                CameraFocalDistance: focalDistance,
                RangePosition: rangeViewPosition,
                RangeDistance: distanceThreshold
            };
        }
        else if (camPosition != null && camYaw != null && camPitch != null) {
            var dir = this.arcRotateToDir(camYaw + Math.PI / 2, camPitch).scale(-1);

            //dir = new BABYLON.Vector3(0, 0, 1);
            //const dir = this.arcRotateToDir(camYaw, camPitch).scale(-1);
            q = this._quatFromUpToDir(dir);
            top = new BABYLON.Vector3(camPosition.X, camPosition.Y, camPosition.Z);

            if (options.camFocalDistance && options.camFocalDistance > 0)
                focalDistance = options.camFocalDistance;

            if (focalDistance == 0)
                focalDistance = 10;

            this._defaultCameraSettings = {
                CameraType: 1,
                CameraPosition: { X: top.x, Y: top.y, Z: top.z },
                CameraRotation: { X: q.x, Y: q.y, Z: q.z, W: q.w },
                CameraHeight: 10,
                CameraFocalDistance: focalDistance,
                RangePosition: rangeViewPosition,
                RangeDistance: distanceThreshold
            };
        }
        else if(zoomBounds) {
            const center = zoomBounds.center;
            let { top, opposite } = this._topCornerAndOpposite(zoomBounds.minimum, zoomBounds.maximum);
            let position = top.clone();
            let camDirection = position.subtract(opposite).normalize();
            q = this._quatFromUpToDir(camDirection);
            focalDistance = BABYLON.Vector3.Distance(position, center);

            this._defaultCameraSettings = {
                CameraType: 1,
                CameraPosition: { X: top.x, Y: top.y, Z: top.z },
                CameraRotation: { X: q.x, Y: q.y, Z: q.z, W: q.w },
                CameraHeight: 10,
                CameraFocalDistance: focalDistance,
                RangePosition: rangeViewPosition,
                RangeDistance: distanceThreshold
            };
        }

        var cameraSettings = this.getDefaultCameraSettings();

        var cameraPos = cameraSettings.CameraPosition;
        var cameraRot = cameraSettings.CameraRotation;

        this.setCameraProperties(cameraPos.X, cameraPos.Y, cameraPos.Z, cameraRot.X, cameraRot.Y, cameraRot.Z, cameraRot.W, cameraSettings.CameraFocalDistance, cameraSettings.CameraHeight, cameraSettings.CameraType, false);
    } else {
        // Range-only update: keep current camera, just update range information
        this._defaultCameraSettings.RangeDistance = distanceThreshold;
        this._defaultCameraSettings.RangePosition = rangeViewPosition;
    }
}

    private async _requestGraphicData(
        ontologyId: string,
        projectId: string,
        assetSets: IAssetSet[],
        assetSetIsolation: boolean,
        ontologyBoundsContext?: IOntologyBoundsContext,
        camPosition?: IVector3D,
        camPitch?: number,
        camYaw?: number,
        rangeViewPosition?: IVector3D,
        distanceThreshold?: number,
        overrideFocalDistance? :number,
        endCb?: () => void
    ): Promise<void> {

        this._initializedOctree = false;
        // Bump generation for a new full viewer load
        const gen = ++this._octreeGeneration;

        const next: typeof this._lastOctreeRequest = {
            ontologyId,
            projectId,
            selectedOntologyId: ontologyBoundsContext?.selectedOntologyId,
            mapBoundsSig: this._sigMapBounds(ontologyBoundsContext),
            rangeViewPosition: rangeViewPosition,
            distanceThreshold: distanceThreshold,
            camPosition: camPosition,
            camYaw: camYaw,
            camPitch: camPitch,
            camFocalDistance: overrideFocalDistance,
            repositionCamera: true,
            assetSetIds: ([]).slice(0)
        };

        await this._setupOctreeData(ontologyId, projectId, assetSets, {
            ontologyBoundsContext,
            camPosition,
            camPitch,
            camYaw,
            rangeViewPosition,
            distanceThreshold,
            camFocalDistance:overrideFocalDistance,
            assetSetIsolation,
            repositionCamera: true
        });

        this._initializedOctree = true;

        if (this._pendingSetupRequest) {
            const req = this._pendingSetupRequest;
            this._pendingSetupRequest = null;

            await this.setupViewerData(
                req.ontologyId,
                req.projectId,
                req.ontologyBoundsContext,
                req.rangePosition,
                req.rangeDistance,
                req.assetSets,
                req.cameraPosition,
                req.cameraDirection,
                req.focalDistance,
                req.repositionCamera
            );
        }

        endCb?.();
    }

    private _isBadNumber(v: number): boolean {
        if (!Number.isFinite(v)) return true;                 // NaN, ±Infinity
        if (Math.abs(v) > THRESHOLD) return true;             // out-of-band sentinel-ish
        if (Math.abs(Math.abs(v) - SENTINEL_APPROX) <= EPS)   // explicit sentinel match
            return true;
        return false;
    }

    private _hasBadBoundingBox(
        bb:
            | BABYLON.BoundingBox
            | BABYLON.BoundingInfo
            | { minimum: BABYLON.Vector3; maximum: BABYLON.Vector3 }
            | { min: BABYLON.Vector3; max: BABYLON.Vector3 }
    ): boolean {
        // Normalize to min/max vectors (BoundingBox has both local and world versions)
        let min: BABYLON.Vector3 | undefined;
        let max: BABYLON.Vector3 | undefined;

        const anyBB: any = bb as any;
        if ("minimumWorld" in anyBB && "maximumWorld" in anyBB) {
            // Prefer world-space bounds if available
            min = anyBB.minimumWorld;
            max = anyBB.maximumWorld;
        } else if ("minimum" in anyBB && "maximum" in anyBB) {
            min = anyBB.minimum;
            max = anyBB.maximum;
        } else if ("min" in anyBB && "max" in anyBB) {
            min = anyBB.min;
            max = anyBB.max;
        }

        if (!min || !max) return true; // can't read extents

        // Any invalid numbers on any axis?
        if (this._isBadNumber(min.x) || this._isBadNumber(min.y) || this._isBadNumber(min.z)) return true;
        if (this._isBadNumber(max.x) || this._isBadNumber(max.y) || this._isBadNumber(max.z)) return true;

        // Inverted bounds?
        if (min.x > max.x || min.y > max.y || min.z > max.z) return true;

        // Optional: reject "nearly empty" boxes (tune epsilon)
        const ex = max.x - min.x, ey = max.y - min.y, ez = max.z - min.z;
        const tiny = 1e-9;
        if (!Number.isFinite(ex + ey + ez)) return true;
        // if (ex < tiny && ey < tiny && ez < tiny) return true;

        return false;
    }

    private arcRotateToDir(alpha: number, beta: number): BABYLON.Vector3 {
        const sinB = Math.sin(beta);
        return new BABYLON.Vector3(
            -Math.cos(alpha) * sinB,
            -Math.cos(beta),
            -Math.sin(alpha) * sinB
        ).normalize();
    }
    private _quatFromUpToDir(dir: BABYLON.Vector3): BABYLON.Quaternion {
        const up = BABYLON.Vector3.Up(); // (0,1,0)
        const d = dir.normalize();
        const dot = BABYLON.Vector3.Dot(up, d);

        if (Math.abs(dot - 1) < 1e-6) {
            return new BABYLON.Quaternion(0, 0, 0, 0);
        }
        if (Math.abs(dot + 1) < 1e-6) {
            return new BABYLON.Quaternion(1, 0, 0, Math.PI);
        }

        const axis = BABYLON.Vector3.Cross(up, d).normalize();
        const angle = Math.acos(dot);

        return new BABYLON.Quaternion(axis.x, axis.y, axis.z, angle);
    }

    private _camDirectionFromQuat(q: BABYLON.Quaternion): BABYLON.Vector3 {
        const cosVal = Math.cos(q.w);
        const sinVal = Math.sin(q.w);

        return new BABYLON.Vector3(
            q.x * q.y * (1 - cosVal) - q.z * sinVal,
            cosVal + q.y * q.y * (1 - cosVal),
            q.x * sinVal + q.y * q.z * (1 - cosVal)
        ).normalize();
    }

    private _topCornerAndOpposite(min: BABYLON.Vector3, max: BABYLON.Vector3) {
        const top = new BABYLON.Vector3(min.x, max.y, min.z);
        const opposite = new BABYLON.Vector3(max.x, min.y, max.z);
        return { top, opposite };
    }

    private _computeBoundingBox(nodes: any[]): BABYLON.BoundingBox {
        const mins = new BABYLON.Vector3(
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY
        );
        const maxs = new BABYLON.Vector3(
            Number.NEGATIVE_INFINITY,
            Number.NEGATIVE_INFINITY,
            Number.NEGATIVE_INFINITY
        );

        for (const n of nodes) {
            mins.x = Math.min(mins.x, n.minPoint.x);
            mins.y = Math.min(mins.y, n.minPoint.y);
            mins.z = Math.min(mins.z, n.minPoint.z);

            maxs.x = Math.max(maxs.x, n.maxPoint.x);
            maxs.y = Math.max(maxs.y, n.maxPoint.y);
            maxs.z = Math.max(maxs.z, n.maxPoint.z);
        }

        return new BABYLON.BoundingBox(mins, maxs);
    }

    private _toBB(
        ext: BABYLON.BoundingBox | BABYLON.BoundingInfo | null | undefined
    ): BABYLON.BoundingBox | null {
        if (!ext) return null;
        return ext instanceof BABYLON.BoundingInfo ? ext.boundingBox : ext;
    }

    private _unionBB(
        a: BABYLON.BoundingBox | null,
        b: BABYLON.BoundingBox | null
    ): BABYLON.BoundingBox | null {
        if (!a && !b) return null;
        if (a && !b) return a;
        if (!a && b) return b;

        const min = new BABYLON.Vector3(
            Math.min(a.minimum.x, b.minimum.x),
            Math.min(a.minimum.y, b.minimum.y),
            Math.min(a.minimum.z, b.minimum.z)
        );
        const max = new BABYLON.Vector3(
            Math.max(a.maximum.x, b.maximum.x),
            Math.max(a.maximum.y, b.maximum.y),
            Math.max(a.maximum.z, b.maximum.z)
        );
        return new BABYLON.BoundingBox(min, max);
    }

    /** Combine a BoundingBox (octree) and a BoundingInfo (tiles) into one BoundingBox. */
    private _combineContentExtents(
        octreeBox: BABYLON.BoundingBox | null,
        tileInfo: BABYLON.BoundingInfo | null
    ): BABYLON.BoundingBox | null {
        const a = this._toBB(octreeBox);
        const t = this._toBB(tileInfo);
        return this._unionBB(a, t);
    }

    private _getZoomBounds(
        contentExtents: BABYLON.BoundingBox | null,
        minBounds?: IVector2D, // X/Z
        maxBounds?: IVector2D  // X/Z
    ): BABYLON.BoundingBox {
        // If we have no content, fall back to bounds (Y=0..0) or a zero box.
        if (!contentExtents) {
            const minY = 0, maxY = 0;
            if (minBounds && maxBounds) {
                return new BABYLON.BoundingBox(
                    new BABYLON.Vector3(minBounds.X, minY, minBounds.Y),
                    new BABYLON.Vector3(maxBounds.X, maxY, maxBounds.Y)
                );
            }
            return new BABYLON.BoundingBox(BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero());
        }

        const minX = contentExtents.minimum.x;
        const maxX = contentExtents.maximum.x;
        const minY = contentExtents.minimum.y;
        const maxY = contentExtents.maximum.y;
        const minZ = contentExtents.minimum.z;
        const maxZ = contentExtents.maximum.z;

        // If caller supplies 2D bounds (X/Z), intersect those with content; keep Y from content.
        if (minBounds && maxBounds) {
            const ixMinX = Math.max(minBounds.X, minX);
            const ixMaxX = Math.min(maxBounds.X, maxX);
            const ixMinZ = Math.max(minBounds.Y, minZ); // bounds.Y corresponds to world Z
            const ixMaxZ = Math.min(maxBounds.Y, maxZ);

            if (ixMinX > ixMaxX || ixMinZ > ixMaxZ) {
                return new BABYLON.BoundingBox(
                    new BABYLON.Vector3(minBounds.X, minY, minBounds.Y),
                    new BABYLON.Vector3(maxBounds.X, maxY, maxBounds.Y)
                );
            }

            return new BABYLON.BoundingBox(
                new BABYLON.Vector3(ixMinX, minY, ixMinZ),
                new BABYLON.Vector3(ixMaxX, maxY, ixMaxZ)
            );
        }

        // No bounds: return the content extents.
        return contentExtents;
    }

    private _getClipBounds(
        contentExtents: BABYLON.BoundingBox | null,
        minBounds?: IVector2D,
        maxBounds?: IVector2D
    ): BABYLON.BoundingBox {
        if (minBounds && maxBounds) {
            const minY = contentExtents?.minimum.y ?? 0;
            const maxY = contentExtents?.maximum.y ?? 0;

            return new BABYLON.BoundingBox(
                new BABYLON.Vector3(minBounds.X, minY, minBounds.Y),
                new BABYLON.Vector3(maxBounds.X, maxY, maxBounds.Y)
            );
        }

        if (contentExtents) {
            return contentExtents;
        }

        return new BABYLON.BoundingBox(BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero());
    }

    private _getSceneCenter(
        bounds: BABYLON.BoundingBox | null
    ): BABYLON.Vector3 {
        const center =
            bounds?.center?.clone() ??
            new BABYLON.Vector3();

        center.y += 100; // Raise the camera slightly above the geometry
        return center;
    }

    private _fetchOctree(ontologyId: string, projectId: string): Promise<any[]> {
        return new Promise((resolve, reject) => {
            Viewer3DActions.getOctree(ontologyId, projectId, [], resolve);
        });
    }
    private _fetchAssetSetOctrees(assetSetIDs:string[])
    {
        return new Promise((resolve, reject) => {
            Viewer3DActions.getAssetSetOctrees(assetSetIDs, resolve);
        });
    }

    private _fetchAppearances(ontologyId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            Viewer3DActions.getAppearances(ontologyId, resolve);
        });
    }

    private _sliceGraphicData(dataArray: any[], sliceSize: number): any[] {
        // It is currenly the case that the graphic service may return slightly more primitives than were requested. The adjusted slice size is in place
        // so that small leftover slices are appended to the final slice.
        var adjustedSliceSize = dataArray.length <= (1.5 * sliceSize) ? 1.5 * sliceSize : sliceSize;

        return dataArray.splice(0, adjustedSliceSize);
    }
    private _requestAppearances(ontologyId: string, projectId: string, cb?: any) {
        var that = this;
        this._eventManager.fireEvent(Viewer3DEvents.RETRIEVING_DATA as any, null);
        Viewer3DActions.getAppearances(ontologyId, function (data: any) {
            that._appearanceData = data;

            if (cb !== undefined) {
                cb();
            }
        });
    }

    private _zoomAreaFinished() {
        this._drawCanvas2d.style.pointerEvents = "none";
        this._isZoomArea = false;
        this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.NAVIGATE);
    }

    private _focuseFinished() {
        this._isFocusing = false;
        this._viewerEngine.sceneManager.setPickMode(this._pickMode);
    }

    private _registerEvents() {
        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.CAMERAFOCUSED, this._focuseFinished);
        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.DISPLAYFINISHED, this._displayCountUpdate);
        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.DISPLAYMEMORYLIMIT, this._displayMemoryLimit);
        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.STARTBLOCKINGOPERATION, this._fireStartBlockingOperation);
        this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.ENDBLOCKINGOPERATION, this._fireEndBlockingOperation);
    }

    private _setDisplayMode(mode: Enumerations.MaterialEdgeMode) {
        this._viewerEngine.meshManager.setMaterialEdgeMode(mode);
        this._viewerEngine.materialManager.setMaterialEdgeMode(mode);
    }

    private _setCameraMode(mode: Enumerations.CameraMode) {
        this._viewerEngine.sceneManager.setCameraMode(mode);
    }

    public addObjects(appearances: Interfaces.Interfaces.IAppearanceObject[], objs: Interfaces.Interfaces.IAssetObject[]) {
        this._eventManager.fireEvent(Viewer3DEvents.RETRIEVING_DATA as any, null);
        this._appearanceData = appearances;
        this._loadMaterials();
        this._currentAssetCount = 0;
        this._totalAssetCount = objs.length;
        this._eventManager.fireEvent(Viewer3DEvents.DISPLAY_STARTED as any, { AssetObjectCount: objs.length });

        this._viewerEngine.displayManager.processDisplayData(objs);
    }

    public setContentLayers(contentLayers: ContentLayers) {
        this._viewerEngine.displayManager.setContentLayers(contentLayers);
    }

    public addLabels(labels: Interfaces.Interfaces.ILabelDataObject[]) {
        this._viewerEngine.displayManager.displayLabels(labels);
    }

    public setAnnotations(tags: IAnnotationDisplayItem[]) {
        // Restore the labels' source appearance before annotation reconciliation can
        // rebuild them and discard the cached pre-override colors (for example, the
        // gold Knowledge Book manage-mode appearance).
        this._viewerEngine.labelManager.resetAllAnnotationColorization();
        this._lastColorizeSig = null;
        this._viewerEngine.labelManager.initializeAnnotations(tags);
    }

    public setShowOccludedTags(show: boolean): void {
        this._viewerEngine.labelManager.setShowOccludedTags(show);
    }

    public async setAssemblyTemplates(assemblyTemplates: IAssemblyTemplate[]) {
        
        await this._viewerEngine.assemblyManager.setAssemblyTemplates(assemblyTemplates);
    }

    public async setAssemblies(assemblies: IAssembly[]) {
        await this._viewerEngine.assemblyManager.updateAllAssemblies(assemblies);
    }

    public setAssemblyCreationTemplate(assemblyCreationTemplate: any) {
        if (assemblyCreationTemplate)
            this._viewerEngine.assemblyManager.beginAssemblyCreation(assemblyCreationTemplate.ID);
        else
            this._viewerEngine.assemblyManager.endAssemblyCreation();
    }

    public setScanPositions(scanPositions: any) {
        this._viewerEngine.scanPositionManager.setPositions(scanPositions);
    }
    public highlightScanPosition(scanPosition: any) {
        this._viewerEngine.scanPositionManager.setHighlighted(scanPosition);
    }
    public setScanPositionSphereColor(color: string) {
        this._viewerEngine.scanPositionManager.setScanSphereColor(color);
    }
    public getScanPositionSphereColor(): string {
        return this._viewerEngine.scanPositionManager.getScanSphereColor();
    }
    public setImageSphereColor(color: string) {
        this._viewerEngine.scanPositionManager.setImageSphereColor(color);
    }
    public getImageSphereColor(): string {
        return this._viewerEngine.scanPositionManager.getImageSphereColor();
    }

    public clearViewer() {
        this._viewerEngine.clearAllGraphicContent();
    }

    public registerEvent(eventType: string, cb: (e: Interfaces.Interfaces.IEventObject) => void) {
        switch (eventType) {
            //These are viewer events
            case Viewer3DEvents.DISPLAY_STARTED:
            case Viewer3DEvents.RETRIEVING_DATA:
            case Viewer3DEvents.VIEWER_FOCUSING:
            case Viewer3DEvents.CAMERA_MODE_CHANGED:
            case Viewer3DEvents.DISPLAY_MEMORY_LIMIT:
            case Viewer3DEvents.VIEWER_BUSY:
            case Viewer3DEvents.VIEWER_IDLE:
            case Viewer3DEvents.VIEWER_RESIZED:
                this._eventManager.registerEvent(eventType as any, cb);
                break;
            case Viewer3DEvents.DISPLAY_FINISHED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.DISPLAYFINISHED, cb);
                break;
            //These are engine events
            case Viewer3DEvents.DISPLAY_IN_PROGRESS:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.DISPLAYFINISHED, cb);
                break;
            case Viewer3DEvents.SELECTION_CHANGED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.SELECTIONCHANGED, cb);
                break;
            case Viewer3DEvents.SELECTION_MODE_CHANGED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.SELECTIONMODECHANGED, cb);
                break;
            case Viewer3DEvents.VIEWER_FOCUSED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.CAMERAFOCUSED, cb);
                break;
            case Viewer3DEvents.VIEWER_BOX_ZOOMED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.CAMERABOXZOOMED, cb);
                break;
            case Viewer3DEvents.MEASUREMENT_ADDED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.MEASUREMENTCREATED, cb);
                break;
            case Viewer3DEvents.CAMERA_CHANGED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.CAMERACHANGED, cb);
                break;
            case Viewer3DEvents.POINT3D_CLICKED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.POINT3DCLICKED, cb);
                break;
            case Viewer3DEvents.VOLUME_TAG_CHANGED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.VOLUME_TAG_CHANGED, cb);
                break;
            case Viewer3DEvents.SCAN_POSITION_HIGHLIGHTED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.SCANPOSITIONHIGHLIGHTED, cb);
                break;
            case Viewer3DEvents.SCAN_POSITION_RIGHT_CLICKED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.SCANPOSITIONRIGHTCLICKED, cb);
                break;
            case Viewer3DEvents.ASSEMBLY_CREATED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.ASSEMBLYCREATED, cb);
                break;
            case Viewer3DEvents.ASSEMBLY_UPDATED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.ASSEMBLYUPDATED, cb);
                break;
            case Viewer3DEvents.VIEWER_SETTING_CHANGED:
                this._viewerEngine.eventManager.registerEvent(Enumerations.EventType.SETTINGCHANGED, cb);
                break;
        }
    }

    public dispose() {
        Viewer3DActions.clearRequests();
        this._eventManager.dispose();
        this._removeEventListeners();
        this._viewerEngine.dispose();
        this._clearViewer();

        this._appearanceData = [];
        this._eventManager = null;
        this._viewerEngine = null;
    }

    private _displayCountUpdate(percentageDisplayed: number) {
        this._totalAssetCount = 100;
        this._currentAssetCount = percentageDisplayed;
    }

    private _displayMemoryLimit(percentage: number) {

        this._eventManager.fireEvent(Viewer3DEvents.DISPLAY_MEMORY_LIMIT as any, percentage);
    }

    public requestData(assetObjectListId: string, projectId: string, namedLists: Interfaces.Interfaces.OverrideNamedList[], ontologyBoundsContext?: IOntologyBoundsContext, camPosition?: IVector3D, camPitch?: number, camYaw?: number, rangePosition?: IVector3D, distanceThreshold?: number, overrideFocalDistance?: number, assetSets?: IAssetSet[], assetSetIsolation?: boolean, cb?: () => void) {
        var that = this;
        that._requestGraphics(assetObjectListId, projectId, ontologyBoundsContext, camPosition, camPitch, camYaw, rangePosition, distanceThreshold, overrideFocalDistance, assetSets, assetSetIsolation, cb);
    }

    public setSelectedAssetSet(assetSet: IAssetSet) {
        // get all assets objects to selected from all hierarchy items in asset set.
        
    }
    public async setupViewerData(
        ontologyId: string,
        projectId: string,
        ontologyBoundsContext?: IOntologyBoundsContext,
        rangePosition?: IVector3D,
        rangeDistance?: number,
        assetSets?: IAssetSet[],
        cameraPosition?: IVector3D,
        cameraDirection?: IVector3D,
        focalDistance?: number,
        repositionCamera: boolean = false
    ): Promise<void> {

        if (!this._initializedOctree) {
            this._pendingSetupRequest = {
                ontologyId,
                projectId,
                ontologyBoundsContext,
                rangePosition,
                rangeDistance,
                assetSets,
                repositionCamera,
                cameraPosition,
                cameraDirection,
                focalDistance
            };
            return;
        }

        var camPitch = null;
        var camYaw = null;

        if (cameraDirection) {
            var yawPitch = this._dirToYawPitch(cameraDirection.X, cameraDirection.Y, cameraDirection.Z);
            camPitch = yawPitch.pitch;
            camYaw = yawPitch.yaw;
        }

        this._initializedOctree = false;

        await this._setupOctreeData(ontologyId, projectId, assetSets, {
            ontologyBoundsContext,
            rangeViewPosition: rangePosition,
            distanceThreshold: rangeDistance,
            camPosition: cameraPosition,
            camDirection: cameraDirection,
            camPitch: camPitch,
            camYaw: camYaw,
            camFocalDistance: focalDistance,
            repositionCamera
        });

        this._initializedOctree = true;

        if (this._pendingSetupRequest) {
            const req = this._pendingSetupRequest;
            this._pendingSetupRequest = null;

            this.setupViewerData(
                req.ontologyId,
                req.projectId,
                req.ontologyBoundsContext,
                req.rangePosition,
                req.rangeDistance,
                req.assetSets,
                req.cameraPosition,
                req.cameraDirection,
                req.focalDistance,
                req.repositionCamera
            );
        }
    }

    private _dirToYawPitch(x: number, y: number, z: number) {
        // normalize (optional but good)
        const len = Math.hypot(x, y, z);
        if (len === 0) return { yaw: 0, pitch: 0 };

        const nx = x / len;
        const ny = y / len;
        const nz = z / len;

        // yaw: angle in XZ plane, measured from +Z toward +X
        const yaw = Math.atan2(nx, nz);

        // pitch: look up/down; positive when looking up
        // clamp to avoid NaNs from floating error
        const pitch = Math.asin(Math.max(-1, Math.min(1, ny)));

        return { yaw, pitch }; // radians
    }

    public requestGraphicAppearance(assetObjectListId: string, projectId: string, namedLists: Interfaces.Interfaces.OverrideNamedList[], defaultNamedList: boolean, cb?: any) {

        var that = this;
        that.changeAssetAppearanceOverrides(namedLists, defaultNamedList);
    }

    public focusAsset() {
        this._eventManager.fireEvent(Viewer3DEvents.VIEWER_FOCUSING as any, null);
        this._isFocusing = true;
        this._viewerEngine.sceneManager.setPickMode(Enumerations.PickMode.FOCUS);
    }

    public exportSceneToGLTF() {
        this._viewerEngine.sceneManager.exportSceneToGLTF();
    }

    public exportSceneToGLB(errorCb?: any) {
        if (!this._viewerEngine.displayManager.isDisplaying()) {
            this._viewerEngine.sceneManager.exportSceneToGLB();
        }
        else {
            errorCb();
        }
    }

    public exportSceneToOBJ(errorCb?: any) {
        if (!this._viewerEngine.displayManager.isDisplaying()) {
            this._viewerEngine.sceneManager.exportSceneToOBJ();
        }
        else {
            errorCb();
        }
    }

    public stopFocus() {
        this._focuseFinished();
    }

    public stopZoomArea() {
        this._zoomAreaFinished();
    }

    public setPanMode() {
        if (!this._isZoomArea)
            this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.NAVIGATE);
        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Pan);
        this._pickMode = Enumerations.PickMode.SELECT;
        if (!this._isFocusing && !this._isZoomArea) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }

    public setCrosshairEnabled(enabled: boolean) {
        this._viewerEngine.sceneManager.setCrosshairEnabled(enabled);
    }

    public setCameraInvertY(invert: boolean) {
        this._viewerEngine.sceneManager.setCameraInvertY(invert);
    }

    public setCameraRotationSensitivity(sensitivity: number) {
        this._viewerEngine.sceneManager.setCameraRotationSensitivity(sensitivity);
    }

    public setCameraVelocitySensitivity(sensitivity: number) {
        this._viewerEngine.sceneManager.setCameraVelocitySensitivity(sensitivity);
    }

    public focusCameraInput() {
        this._viewerEngine.sceneManager.focusCameraInput();
    }

    public setUniversalMode() {
        if (!this._isZoomArea)
            this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.NAVIGATE);
        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Universal);
        this._pickMode = Enumerations.PickMode.SELECT;
        if (!this._isFocusing && !this._isZoomArea) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }

    public setFlyMode() {
        if (!this._isZoomArea)
            this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.NAVIGATE);
        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Fly);
        this._pickMode = Enumerations.PickMode.SELECT;
        if (!this._isFocusing && !this._isZoomArea) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }

    public setNavMode() {
        if (!this._isZoomArea)
            this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.NAVIGATE);
        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Examine);
        this._pickMode = Enumerations.PickMode.SELECT;
        if (!this._isFocusing && !this._isZoomArea) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }
    public setWalkMode() {

        this._viewerEngine.sceneManager.setCameraNavigationMode(Enumerations.CameraNavigationMode.Walk);
        this._pickMode = Enumerations.PickMode.SELECT;
        if (!this._isFocusing && !this._isZoomArea) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }

    public exitFollowCamera() {
        this._viewerEngine.sceneManager.exitFollowCamera();
    }

    public setMeasurementMode() {
        this._pickMode = Enumerations.PickMode.MEASUREMENT;
        if (!this._isFocusing) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
    }
    public setMeasurementColor(color: string) {
        this._viewerEngine.measurementManager.setMeasurementColor(color);
    }

    public setMemoryProfile(profile: string) {
        this._viewerEngine.displayManager.setMemoryProfile(profile);
    }

    public setMeshMemoryProfile(profile: string) {
        this._viewerEngine.displayManager.setMeshMemoryProfile(profile);
    }

    public getMeasurementColor(): string {
        return this._viewerEngine.measurementManager.getMeasurementColor();
    }

    public setViewerClickMode(mode: "select" | "point3D") {
        this._pickMode = mode === "point3D" ? Enumerations.PickMode.POINT3D : Enumerations.PickMode.SELECT;
        if (!this._isFocusing) {
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }

        if (mode === "point3D") {
            this._canvas.parentElement.style.cursor = 'crosshair';
        }
        else {
            this._canvas.parentElement.style.cursor = '';
        }
    }

    public setAssemblyEditMode(mode: "move" | "scale" | "none") {
        if (mode === "move") {
            this._viewerEngine.assemblyManager.setSelectedAssemblyEdit();
        } else if (mode === "scale") {
            this._viewerEngine.assemblyManager.setSelectedAssemblyScale();
        } else {

        }
    }

    public setMultiSelectEnabled(enabled : boolean) {
        this.unhighlightAllObjects(true);
        this._viewerEngine.selectionManager.setMultiSelectEnabled(enabled);
    }

    public setTagEditMode(mode: "move" | "scale" | "none") {
        this._viewerEngine.labelManager.setPositioningMode(mode);
    }
    public zoomArea() {
        this._drawCanvas2d.style.pointerEvents = "all";
        this._isZoomArea = true;
        this._viewerEngine.sceneManager.setClickMode(Enumerations.ClickMode.REGION);
    }

    public getmemoryMinimum() {

        return this._viewerEngine.displayManager.getMemoryMinimum();
    }
    public getmemoryMaximum() {

        return this._viewerEngine.displayManager.getMemoryMaximum();
    }

    public zoomAll() {
        this._viewerEngine.sceneManager.viewAll();
    }
    public zoomIn() {
        this._viewerEngine.sceneManager.dollyIncrement();
    }

    public zoomOut() {
        this._viewerEngine.sceneManager.dollyDecrement();
    }

    public zoomToMeasurements(projectId: string, objectIds: string[], markedPointIds: string[]) {
        var that = this;

        this._viewerEngine.sceneManager.viewObjects(objectIds);
    }

    public alignToScan(position: IVector3D, direction: IQuaternion) {

        var that = this;

        that._viewerEngine.sceneManager.setCameraDirection(new BABYLON.Vector3(position.X / 1000, position.Y / 1000, position.Z / 1000), new BABYLON.Quaternion(direction.X, direction.Y, direction.Z, direction.W));
    }

    public allowPick(allow: boolean) {
        var that = this;

        that._viewerEngine.sceneManager.setAllowPick(allow);
    }
    public setCameraMouseZoomFactor(factor: number) {
        var that = this;

        that._viewerEngine.sceneManager.setCameraMouseZoomFactor(factor);
    }

    public setCamraMouseWheelFactor(factor: number) {
        var that = this;

        that._viewerEngine.sceneManager.setCamraMouseWheelFactor(factor);
    }

    public setCameraPinchFactor(factor: number) {
        var that = this;

        that._viewerEngine.sceneManager.setCameraPinchFactor(factor);
    }


    public zoomToScan(projectId: string, scanId: string) {
        var that = this;

        var scanPosition = this._viewerEngine.markedPointsManager.getScanPosition(scanId);

        if (scanPosition) {

            var mpPadding = 400;

            var minX = scanPosition.X - mpPadding / 2;
            var minY = scanPosition.Y - mpPadding / 2;
            var minZ = scanPosition.Z - mpPadding / 2;

            var maxX = scanPosition.X + mpPadding / 2;
            var maxY = scanPosition.Y + mpPadding / 2;
            var maxZ = scanPosition.Z + mpPadding / 2;

            that._viewerEngine.sceneManager.zoomToExtents(new BABYLON.Vector3(minX / 1000, minY / 1000, minZ / 1000), new BABYLON.Vector3(maxX / 1000, maxY / 1000, maxZ / 1000));
        }
    }

    public zoomToExtent(min: IVector3D, max: IVector3D) {


        var that = this;

        var minX = min.X;
        var minY = min.Y;
        var minZ = min.Z;

        var maxX = max.X;
        var maxY = max.Y;
        var maxZ = max.Z;

        that._viewerEngine.sceneManager.zoomToExtents(new BABYLON.Vector3(minX / 1000, minY / 1000, minZ / 1000), new BABYLON.Vector3(maxX / 1000, maxY / 1000, maxZ / 1000));
    }

    public zoomToObjects(projectId: string, objectIds: string[], markedPointIds: string[]) {

        var that = this;
        Viewer3DActions.getObjectExtents(projectId, objectIds, markedPointIds,
            function (data: any) {

                if (markedPointIds.length > 0) {

                    var mpPadding = 400;
                    data.MinExtents.X -= mpPadding / 2;
                    data.MinExtents.Y -= mpPadding / 2;
                    data.MinExtents.Z -= mpPadding / 2;

                    data.MaxExtents.X += mpPadding / 2;
                    data.MaxExtents.Y += mpPadding / 2;
                    data.MaxExtents.Z += mpPadding / 2;
                }

                that._viewerEngine.sceneManager.zoomToExtents(new BABYLON.Vector3(data.MinExtents.X / 1000, data.MinExtents.Y / 1000, data.MinExtents.Z / 1000), new BABYLON.Vector3(data.MaxExtents.X / 1000, data.MaxExtents.Y / 1000, data.MaxExtents.Z / 1000));
            }
        );
    }

    public setCamera(cameraPosition: BABYLON.Vector3, focusPosition: BABYLON.Vector3, skipAnimation: boolean) {
        return this._viewerEngine.sceneManager.setCamera(cameraPosition, focusPosition, skipAnimation);
    }

    public focusCamera(focusPosition: BABYLON.Vector3) {
        return this._viewerEngine.sceneManager.focusCamera(focusPosition);
    }

    public highlightObjects(ids: string[], clearAll: boolean) {
        if (clearAll && !this._viewerEngine.selectionManager.multiSelectEnabled()) {
            this.unhighlightAllObjects();
        }
        if (ids.length === 1) {
            this.highlightSingleObject(ids[0]);
        } else {
            this._viewerEngine.selectionManager.selectObjects(ids, true, Enumerations.PickObjectType.Unknown, false);
        }
    }

    public unhighlightObjects(ids: string[], objectType : Enumerations.PickObjectType) {
        this._viewerEngine.selectionManager.unselectObjects(ids, false);
    }

    private _cloneOctreeRoots(roots: any[]): any[] {
        return (roots || []).map(root => JSON.parse(JSON.stringify(root)));
    }

    private async _buildAssetSetRoots(assetSets: IAssetSet[] = []): Promise<any[]> {
        if (!assetSets?.length) {
            return [];
        }

        const ordinarySets = assetSets.filter(assetSet =>
            assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags);
        if (!ordinarySets.length) return [];
        const cacheScope = this._cachedOctreeProjectId ?? '';
        const cacheKey = (assetSetID: string) => `${cacheScope}|${assetSetID.toLocaleLowerCase()}`;
        ordinarySets.forEach((assetSet, index) => {
            this._viewerEngine.assetSetManager.registerAssetSet(assetSet.ID, index + 1);
        });

        const missing = ordinarySets.filter(assetSet => {
            const cached = this._assetSetOctreeRootCache.get(cacheKey(assetSet.ID));
            return !cached || cached.versionToken !== (assetSet.VersionToken ?? '');
        });
        if (missing.length) {
            const assetSetOctrees = await this._fetchAssetSetOctrees(missing.map(set => set.ID)) as any[];
            const returnedAssetSetIDs = new Set(
                (assetSetOctrees || [])
                    .map(row => row?.AssetSetID?.toLocaleLowerCase())
                    .filter(Boolean)
            );
            const omitted = missing.filter(assetSet =>
                !returnedAssetSetIDs.has(assetSet.ID.toLocaleLowerCase()));
            if (omitted.length) {
                throw new Error(`Asset Set octree response omitted: ${omitted.map(set => set.ID).join(', ')}`);
            }
            assetSetOctrees.forEach(as => {
                if (!as?.TreeData) return null;

                try {
                    const root = typeof as.TreeData === "string"
                        ? JSON.parse(as.TreeData)
                        : JSON.parse(JSON.stringify(as.TreeData));

                    root.AssetSetID = as.AssetSetID;
                    root.assetSetId = as.AssetSetID;
                    const source = missing.find(set => set.ID.toLocaleLowerCase() === as.AssetSetID?.toLocaleLowerCase());
                    this._assetSetOctreeRootCache.set(cacheKey(as.AssetSetID), {
                        versionToken: source?.VersionToken ?? '',
                        root,
                    });
                } catch (e) {
                    console.warn("Failed to parse AssetSet TreeData for", as?.AssetSetID, e);
                    throw e;
                }
            });
        }

        return ordinarySets.flatMap(assetSet => {
            const cached = this._assetSetOctreeRootCache.get(cacheKey(assetSet.ID));
            return cached ? [JSON.parse(JSON.stringify(cached.root))] : [];
        });
    }

    public async refreshAssetSetOctrees(assetSets: IAssetSet[] = [], assetSetIsolation: boolean = false): Promise<void> {
        if (!this._cachedOctreeProjectId || !this._cachedBaseOctreeData?.length) {
            return;
        }

        const generation = ++this._assetSetOctreeGeneration;
        const projectID = this._cachedOctreeProjectId;
        const rootUpdate = planRetainedAssetSetRootUpdate(
            assetSets,
            this._registeredAssetSetRootVersions,
            this._currentFallbackAssetLayerID,
            assetSetIsolation
        );
        const {
            ordinarySets,
            fallbackAssetLayerID,
            canAdoptFallbackInPlace,
            requiresFullRebuild,
            missingSets
        } = rootUpdate;

        if (canAdoptFallbackInPlace) {
            (this._viewerEngine.displayManager as any).assignUnmappedAssetPagesToLayer(fallbackAssetLayerID);
            this._currentFallbackAssetLayerID = fallbackAssetLayerID;
        }

        if (!requiresFullRebuild) {
            if (!missingSets.length) return;

            const missingRoots = await this._buildAssetSetRoots(missingSets);
            if (generation !== this._assetSetOctreeGeneration || projectID !== this._cachedOctreeProjectId) return;

            for (const root of missingRoots) {
                root.assetLayerId = root.AssetSetID || root.assetSetId;
            }
            const addedBlocks = (this._viewerEngine.displayManager as any).appendOctreesData(
                missingRoots,
                this._cachedMinDisplayBounds,
                this._cachedMaxDisplayBounds
            );
            (this._viewerEngine.sceneManager as any).addCoordinatorOctreeBlocks(
                (this._viewerEngine.displayManager as any).processMemoryPressureDisplay,
                addedBlocks
            );
            for (const assetSet of missingSets) {
                this._registeredAssetSetRootVersions.set(
                    assetSet.ID.toLocaleLowerCase(),
                    assetSet.VersionToken ?? ''
                );
            }
            return;
        }
        const baseOctreeData = this._cloneOctreeRoots(this._cachedBaseOctreeData);
        const assetSetRoots = await this._buildAssetSetRoots(assetSets);
        if (generation !== this._assetSetOctreeGeneration || projectID !== this._cachedOctreeProjectId) return;
        const octreeData: any[] = [
            ...baseOctreeData,
            ...assetSetRoots
        ];

        if (!octreeData.length) {
            return;
        }

        for (let i = 0; i < octreeData.length; i++) {
            const node = octreeData[i] as any;
            const assetSetId = node.AssetSetID || node.assetSetId;
            node.assetLayerId = assetSetId;

            if (node.assetLayerId == null && fallbackAssetLayerID) {
                node.assetLayerId = fallbackAssetLayerID;
            }
        }

        const setupOctree = (this._viewerEngine.displayManager as any).setupOctreesDataPreserveContent(
            octreeData,
            this._cachedMinDisplayBounds,
            this._cachedMaxDisplayBounds
        );

        (this._viewerEngine.sceneManager as any).setCoordinatorOctree(
            (this._viewerEngine.displayManager as any).processMemoryPressureDisplay,
            setupOctree
        );
        this._registeredAssetSetRootVersions.clear();
        for (const assetSet of ordinarySets) {
            this._registeredAssetSetRootVersions.set(
                assetSet.ID.toLocaleLowerCase(),
                assetSet.VersionToken ?? ''
            );
        }
        this._currentFallbackAssetLayerID = fallbackAssetLayerID;
    }

    public populateAssetSets(assetSetData: { id: string, assetIds: string[], isAll3DAssets: boolean }[]) {

        this._lastColorizeSig = null;
        (this._viewerEngine.assetSetManager).setAssetSets(assetSetData);
        this._viewerEngine.displayManager.refreshAssetGeometryOwnership();
    }

    public applyAssetSetMembershipDelta(
        activatedSets: { id: string, assetIds: string[], isAll3DAssets: boolean }[],
        deactivatedSetIds: string[]
    ) {
        this._lastColorizeSig = null;
        this._viewerEngine.assetSetManager.applyAssetSetMembershipDelta(
            activatedSets,
            deactivatedSetIds
        );
        this._viewerEngine.displayManager.refreshAssetGeometryOwnership();
    }

    public colorizeAssetSets(assetSetData: {
        id: string;
        priority: number;
        colorKey?: number;
        hidden?: boolean;
        isAll3DAssets?: boolean;
        materialID?: string | null;
        tagIDs: string[];
    }[], strictIsolation: boolean = false) {
        const stateSig = strictIsolation ? 'strict' : 'sets';
        const sig = `${stateSig}|${this._sigColorization(assetSetData)}`;
        const isDirty = sig !== this._lastColorizeSig;

        try {
            this._viewerEngine.labelManager.resetAllAnnotationColorization();

            if (isDirty) {
                this._lastColorizeSig = sig;
                this._viewerEngine.displayManager.applyKnowledgeViewAssetSetsAndRefresh(
                    assetSetData,
                    strictIsolation
                );
            }

            for (const def of assetSetData ?? []) {
                if (!def.hidden && def.colorKey != null && def.tagIDs?.length > 0) {
                    this._viewerEngine.labelManager.colorizeAnnotations(def.tagIDs, def.colorKey);
                }
            }
        } catch (e) {
            this._lastColorizeSig = null;
            throw e;
        }
    }


    public destroyAllAssetSets() {
        this._lastColorizeSig = null;
        (this._viewerEngine.assetSetManager).destroyAllAssetSets();

        this._viewerEngine.labelManager.resetAllAnnotationColorization();
    }
    public displayKVAssetSets(assetSetData: IKnowledgeViewAssetSet[]) {
        //(this._viewerEngine.displayManager).applyKnowledgeViewAssetSetsAndRefresh(assetSetData);
    }

    public setAdvancedMaterialTunings(tunings: Record<string, IAdvancedMaterialTuning>): void {
        this._viewerEngine.materialManager.setAdvancedMaterialTunings(tunings);
    }

    public highlightAssetSetObjects(ids: string[], clearAll: boolean) {
        if (clearAll && !this._viewerEngine.selectionManager.multiSelectEnabled()) {
            this.unhighlightAllObjects();
        }
        if (ids.length === 1) {
            this.highlightSingleObject(ids[0]);
        } else {
            this._viewerEngine.selectionManager.selectObjects(ids, true, Enumerations.PickObjectType.Unknown, false);
        }


    }


    enterFollowCamera(routeId: string, junctionId: string, exitCameraCallback: () => void) {
        this._viewerEngine.sceneManager.enterFollowCamera(routeId, junctionId, exitCameraCallback);
    }
    focusFollowCamera() {
        this._viewerEngine.sceneManager.focusFollowCamera();
    }
    zoomToJunction(routeId: string, junctionId: string) {
        this._viewerEngine.sceneManager.zoomToJunction(routeId, junctionId);
    }


    public hideRoute(route: Interfaces.Interfaces.IRoute) {
        this._viewerEngine.sceneManager.hideRoute(route);
    }

    public displayRoute(route: Interfaces.Interfaces.IRoute) {
        this._viewerEngine.sceneManager.displayRoute(route);
    }

    public hideObjects(ids: string[]) {

        // The viewer only knows the original material for the asset based on it's current material. We have to clear the selection when hiding/unhiding in case the thing to be hidden is
        // highlighted. Otherwise, the unhide would always revert it back to the selection matereial, even if it is no longer highlighted.
        var selectedObjects = this._viewerEngine.selectionManager.getSelectedAssetIds();
        this.unhighlightAllObjects();
        this._viewerEngine.meshManager.hideObjects(ids, false);
        this._viewerEngine.selectionManager.selectObjects(selectedObjects, false, Enumerations.PickObjectType.Unknown, false);
    }

    public unhideObjects(ids: string[]) {
        var selectedObjects = this._viewerEngine.selectionManager.getSelectedAssetIds();
        this.unhighlightAllObjects();
        this._viewerEngine.meshManager.unhideObjects(ids, false);
        this._viewerEngine.selectionManager.selectObjects(selectedObjects, false, Enumerations.PickObjectType.Unknown, false);
    }

    public highlightSingleObject(id: string) {
            if(!this._viewerEngine.selectionManager.multiSelectEnabled())
                this.unhighlightAllObjects();

        var isSelected = this._viewerEngine.selectionManager.selectSingleObject(id, true, false, Enumerations.PickObjectType.Unknown);

        this._viewerEngine.markedPointsManager.pickMarkedPoint(id, isSelected, false, true);


    }

        public unhighlightAssets(assetIDs : string[], type : Enumerations.PickObjectType ) {
            this._viewerEngine.selectionManager.unselectAssets(assetIDs, type, false);
        }

    public unhighlightAllObjects(fireEvents :boolean = false) {
        this._viewerEngine.markedPointsManager.unPickAllMarkedPoints();

        // Don't emit events here, since this should only be called from a store update event.
        this._viewerEngine.selectionManager.unselectAllAssets(fireEvents);
    }

    public getMeshKeysOfAsset(assetId: string): string[] {

        return this._viewerEngine.meshManager.getMasterKeysOfObject(assetId);
    }
    public getOverrideMaterialOfAsset(assetId: string): string[] {

        return this._viewerEngine.meshManager.getOverrideMaterialOfObject(assetId);
    }

    public viewUp() {
        //BABYLON should not be here....
        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(0, -1, 0));
    }

    public viewDown() {
        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(0, 1, 0));
    }

    public viewNorth() {


        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(0, 0, -1));
    }

    public viewSouth() {
        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(0, 0, 1));
    }

    public viewEast() {
        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(-1, 0, 0));
    }

    public viewWest() {
        this._viewerEngine.sceneManager.alignCamera(new BABYLON.Vector3(1, 0, 0));
    }

    public changeAssetsMaterial(currentAssetMaterialId: string, assetIdsToChange: string[], newAssetMaterialId: string) {
        this._viewerEngine.meshManager.changeGraphicObjectsMaterial(currentAssetMaterialId, assetIdsToChange, newAssetMaterialId, false);
    }

    public changeAssetAppearanceOverrides(namedLists: Interfaces.Interfaces.OverrideNamedList[], activateDefaultList: boolean) {

        this._viewerEngine.displayManager.changeAppearanceOverrides(namedLists, activateDefaultList);
    }

    public changeMaterialProperties(materialId: string, rgb: any, alpha: number) {
        let diffuseColor: BABYLON.Color3 = new BABYLON.Color3(rgb.r / 255, rgb.g / 255, rgb.b / 255);
        this._viewerEngine.materialManager.changeMaterialProperties(materialId, diffuseColor, alpha);
    }

    public overrideSelectionMaterial(rgb: any) {
        let diffuseColor: BABYLON.Color3 = new BABYLON.Color3(rgb.r / 255, rgb.g / 255, rgb.b / 255);
        this._viewerEngine.materialManager.overrideSelectionMaterial(diffuseColor);
    }

    public changeViewerSetting(settingName: Enumerations.ViewerSettingType, settingValue: any) {
        this._viewerEngine.eventManager.fireEvent(
            Enumerations.EventType.SETTINGCHANGED as any,
            { Name: settingName, Value: settingValue }
        );
    }

    public setMeshShadeType(shadeType: Enumerations.MeshShadeMode) {
        this._viewerEngine.displayManager.setXRayMode(shadeType === Enumerations.MeshShadeMode.XRAY);
    }

    public setPointShadeType(shadeType: Enumerations.PointShadeMode) {
        this._viewerEngine.meshManager.setPointShadeType(shadeType);
    }

    public setFollowNavMode(free: boolean) {
        this._viewerEngine.sceneManager.setFollowNavMode(free);
    }

    public setBackgroundColorHex(hexColor: string) {
        this._viewerEngine.sceneManager.setBackgroundColor(BABYLON.Color4.FromHexString(hexColor));
    }

    public addScans(ids: string[], positions: IVector3D[]) {
        this._viewerEngine.markedPointsManager.addScans(ids, positions);
    }

    public createScans() {
        this._viewerEngine.markedPointsManager.createScans();
    }

    public displayScans(show: boolean) {
        this._viewerEngine.markedPointsManager.displayScans(show);
    }

    public setBackgroundScene(sceneType: number) {
        this._viewerEngine.sceneManager.setBackgroundScene(sceneType, this._graphicsBaseUrl);
    }

    public setCameraFocalDistance(distance: number) {
        this._viewerEngine.sceneManager.setCameraFocalDistance(distance);
    }

    public setCameraPosition(x: number, y: number, z: number) {
        this._viewerEngine.sceneManager.setCameraPosition(new BABYLON.Vector3(x, y, z));
    }

    public initializeCameraProperties(posX: number, posY: number, posZ: number, x: number, y: number, z: number, w: number, focalDistance: number, cameraHeight: number, mode: number) {

        var type: Enumerations.CameraMode = mode == 1 ? Enumerations.CameraMode.PERSPECTIVE : Enumerations.CameraMode.ORTHOGRAPHIC;

        this._viewerEngine.sceneManager.initializeCameraProperties(new BABYLON.Vector3(posX, posY, posZ), new BABYLON.Quaternion(x, y, z, w), focalDistance, cameraHeight, type);
    }

    public setCameraProperties(posX: number, posY: number, posZ: number, x: number, y: number, z: number, w: number, focalDistance: number, cameraHeight: number, mode: number, animate?: boolean) {

        var type: Enumerations.CameraMode = mode == 1 ? Enumerations.CameraMode.PERSPECTIVE : Enumerations.CameraMode.ORTHOGRAPHIC;

        this._viewerEngine.sceneManager.setCameraProperties(new BABYLON.Vector3(posX, posY, posZ), new BABYLON.Quaternion(x, y, z, w), focalDistance, cameraHeight, type, animate, true);
    }

    public setDisplayModeWireframe() {
        this._setDisplayMode(Enumerations.MaterialEdgeMode.WIREFRAME);
    }

    public setDisplayModeSolid() {
        this._setDisplayMode(Enumerations.MaterialEdgeMode.SOLID);
    }


    public setViewerFit() {

        this._fillStyle = Enumerations.ViewerFillStyle.FIT;
        this._resizeCanvas();
    }

    public setViewerFill() {

        this._fillStyle = Enumerations.ViewerFillStyle.FILL;
        this._resizeCanvas();
    }

    public setPerspectiveCamera() {
        this._setCameraMode(Enumerations.CameraMode.PERSPECTIVE);
        this._eventManager.fireEvent(Viewer3DEvents.CAMERA_MODE_CHANGED as any, Enumerations.CameraMode.PERSPECTIVE);

    }
    public getShaderEnabled(): boolean {
        return this._viewerEngine.sceneManager.getShaderEnabled();
    }

    public getMemoryProfile(): string {
        return this._viewerEngine.displayManager.getMemoryProfile();
    }
    public getTargettedMemoryProfile(): string {
        return this._viewerEngine.displayManager.getTargettedMemoryProfile();
    }

    public getMeshMemoryProfile(): string {
        return this._viewerEngine.displayManager.getMeshMemoryProfile();
    }

    public getTargettedMeshMemoryProfile(): string {
        return this._viewerEngine.displayManager.getTargettedMeshMemoryProfile();
    }
    public getShaderRidge(): number {
        return this._viewerEngine.sceneManager.getShaderRidge();
    }

    public getAmbientIntensity(): number {
        return this._viewerEngine.sceneManager.getAmbientIntensity();
    }
    public getShaderValley(): number {
        return this._viewerEngine.sceneManager.getShaderValley();
    }
    public getLightingMode() {
        return this._lightingMode;
    }

    public setLightingMode(mode: Enumerations.LightingMode) {
        this._viewerEngine.sceneManager.setLightingMode(mode);
        this._lightingMode = mode;
    }

    public setEdgeMode(enabled: boolean) {
        this._viewerEngine.sceneManager.setEdgeMode(enabled);
    }

    public setShaderValley(valley: number) {
        this._viewerEngine.sceneManager.setShaderValley(valley);
    }
    public setShaderRidge(ridge: number) {
        this._viewerEngine.sceneManager.setShaderRidge(ridge);
    }

    public setAmbientIntensity(intensity: number) {
        this._viewerEngine.sceneManager.setAmbientIntensity(intensity);
    }

    public setOrthographicCamera() {
        this._setCameraMode(Enumerations.CameraMode.ORTHOGRAPHIC);
        this._eventManager.fireEvent(Viewer3DEvents.CAMERA_MODE_CHANGED as any, Enumerations.CameraMode.ORTHOGRAPHIC);
    }

    public setMeasurements(measurements: Measurement[]) {
        this._viewerEngine.measurementManager.setMeasurements(measurements);
    }

    public clearMeasurements() {
        this._viewerEngine.measurementManager.clearMeasurements();
    }

    public setMeasurementType(type: MeasurementType) {
        if (type) {
            this._pickMode = Enumerations.PickMode.MEASUREMENT;
            this._viewerEngine.sceneManager.setPickMode(this._pickMode);
        }
        this._viewerEngine.measurementManager.setMeasurementMode(type);
    }

    public displayObjects(ids: string[], show: boolean) {

        this._viewerEngine.markedPointsManager.displayAnnotation(ids, show);

        if (show) {
            this.unhideObjects(ids);
        }
        else {
            this.hideObjects(ids);
        }
    }

    public changeCameraFocalDistance(stepSize: number) {
        this._viewerEngine.sceneManager.changeCameraFocalDistance(stepSize);
    }

    public getScaleRatio() {
        if (this._fillStyle == Enumerations.ViewerFillStyle.FIT && this._canvasSize.height && this._canvasSize.width) {
            return this._canvas.height / this._canvasSize.height;
        }

        return 1;
    }

    public getScreenshotSize(width?: number, height?: number) {

        if(width != null && height != null)
        {
            return {
                width: width, height: height
            };
        }

        var baseWidth = this._canvasSize.width;
        var baseHeight = this._canvasSize.height;
        if (this._fillStyle == Enumerations.ViewerFillStyle.FILL) {
            var size = this.getActualCanvasSize();
            baseWidth = size.width;
            baseHeight = size.height;
        }

        var ssWidth = baseWidth;
        var ssHeight = baseHeight;

        if (width && height) {
            if (ssWidth / ssHeight > width / height) {
                // Scale based on Height
                ssWidth = Math.ceil(((ssWidth * height) / ssHeight));
                ssHeight = height;
            } else {
                ssHeight = Math.ceil(((ssHeight * width) / ssWidth));
                ssWidth = width;
            }
        } else if (width) {
            ssHeight = Math.ceil(((ssHeight * width) / ssWidth));
            ssWidth = width;
        } else if (height) {
            ssWidth = Math.ceil(((ssWidth * height) / ssHeight));
            ssHeight = height;
        }

        return {
            width: ssWidth, height: ssHeight
        };
    }

    public getScreenshot(callbackFn: (base64Image: string) => void, width?: number, height?: number) {
        let { width: ssWidth, height: ssHeight } = this.getScreenshotSize(width, height);

        setTimeout(() => {
            this._viewerEngine.sceneManager.getScreenshot(ssWidth, ssHeight, callbackFn)
        }, 0);
    }

    public getPanoramaImage(callbackFn: (base64Image: string) => void, size: number) {
        setTimeout(() => {
            this._viewerEngine.sceneManager.getPanoramaImage(size, callbackFn);
        }, 0);
    }

    public getCameraSettings() {
        var viewerSettings = this._viewerEngine.sceneManager.getCameraProperties();
        return {
            CameraFocalDistance: viewerSettings.CameraFocalDistance,
            CameraType: viewerSettings.CameraType == 0 ? 1 as any : 2 as any,
            CameraPosition: {
                X: viewerSettings.CameraPosition.x,
                Y: viewerSettings.CameraPosition.y,
                Z: viewerSettings.CameraPosition.z
            },
            CameraHeight: viewerSettings.CameraHeight,
            CameraRotation: {
                W: viewerSettings.Quaternion.w,
                X: viewerSettings.Quaternion.x,
                Y: viewerSettings.Quaternion.y,
                Z: viewerSettings.Quaternion.z
            }

        } as Interfaces.Interfaces.ICameraSettings;
    }

    public getDefaultCameraSettings() {
        return this._defaultCameraSettings;
    }

    public getSceneExtents() {
        return this._viewerEngine.sceneManager.getSceneExtents();
    }

    public setCameraSettings(settings: Interfaces.Interfaces.ICameraSettings) {
        var position = new BABYLON.Vector3(settings.CameraPosition.X, settings.CameraPosition.Y, settings.CameraPosition.Z);
        var quaternion = new BABYLON.Quaternion(settings.CameraRotation.X, settings.CameraRotation.Y, settings.CameraRotation.Z, settings.CameraRotation.W);
        var type: Enumerations.CameraMode = settings.CameraType == 1 ? Enumerations.CameraMode.PERSPECTIVE : Enumerations.CameraMode.ORTHOGRAPHIC;
        this._viewerEngine.sceneManager.setCameraPropertiesWithQuaternion(position, quaternion, settings.CameraFocalDistance, settings.CameraHeight, type);
    }

    public setMemoryCap = (num: number) => {
        this._viewerEngine.displayManager.setMemoryCap(num);
    }

    public getMemoryCap = () => {
        return this._viewerEngine.displayManager.getMemoryCap();
    }

    public setMemoryLevel = (level: Enumerations.MemoryMode) => {
        return this._viewerEngine.displayManager.setMemoryLevel(level);
    }
    public getFillStyle = () => {
        return this._fillStyle;
    }

    private _vec2Equals(a?: IVector2D, b?: IVector2D): boolean {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.X === b.X && a.Y === b.Y;
    }

    private _vec3Equals(a?: IVector3D, b?: IVector3D): boolean {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.X === b.X && a.Y === b.Y && a.Z === b.Z;
    }

    private _hashStrings(values: string[]): string {
        let h = 2166136261;
        for (let i = 0; i < values.length; i++) {
            const s = values[i];
            for (let j = 0; j < s.length; j++) {
                h ^= s.charCodeAt(j);
                h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
            }
            h ^= 44;
            h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
        }
        return (h >>> 0).toString(16);
    }

    private _normalizeAssetSetIds(assetSets?: IAssetSet[] | null): string[] {
        if (!assetSets || assetSets.length === 0) return [];

        return assetSets
            .map(set => {
                const setId = (set as any)?.ID ?? "";

                const hierarchyIds = Array.from(
                    new Set(
                        (((set as any)?.HierarchyItems ?? []) as any[])
                            .map(h => h?.ID)
                            .filter((id): id is string => typeof id === "string" && id.length > 0)
                    )
                ).sort();

                const hash = this._hashStrings(hierarchyIds);

                return `${setId}|${hierarchyIds.length}|${hash}`;
            })
            .sort();
    }

    private _stringArrayEquals(a?: string[] | null, b?: string[] | null): boolean {
        const aa = a ?? [];
        const bb = b ?? [];
        if (aa.length !== bb.length) return false;
        for (let i = 0; i < aa.length; i++) {
            if (aa[i] !== bb[i]) return false;
        }
        return true;
    }

    private _octreeRequestEquals(
        a: typeof this._lastOctreeRequest,
        b: typeof this._lastOctreeRequest
    ): boolean {
        if (!a && !b) return true;
        if (!a || !b) return false;

        return (
            a.ontologyId === b.ontologyId &&
            a.projectId === b.projectId &&
            a.selectedOntologyId === b.selectedOntologyId &&
            a.mapBoundsSig === b.mapBoundsSig &&
            this._vec3Equals(a.rangeViewPosition, b.rangeViewPosition) &&
            this._distanceEquals(a.distanceThreshold, b.distanceThreshold) &&
            this._stringArrayEquals(a.assetSetIds, b.assetSetIds) &&
            a.assetSetIsolation === b.assetSetIsolation
        );
    }

    private _distanceEquals(a?: number, b?: number): boolean {
        // Treat undefined / null / 0 as equivalent
        const na = (a == null || a === 0) ? 0 : a;
        const nb = (b == null || b === 0) ? 0 : b;
        return na === nb;
    }

    private _sigColorization(assetSetData: {
        id: string;
        priority: number;
        colorKey?: number;
        hidden?: boolean;
        materialID?: string | null;
        tagIDs: string[];
    }[]): string {
        const defs = (assetSetData ?? [])
            .map(d => ({
                id: d.id ?? "",
                priority: d.priority ?? 0,
                colorKey: d.colorKey ?? null,
                hidden: !!d.hidden,
                materialID: d.materialID ?? null,
                // order-independent tag signature
                tagSig: (d.tagIDs ?? []).slice().sort().join(",")
            }))
            .sort((a, b) => a.id.localeCompare(b.id));

        return defs.map(d => `${d.id}|${d.priority}|${d.colorKey ?? ""}|${d.hidden ? 1 : 0}|${d.materialID ?? ""}|${d.tagSig}`).join("||");
    }

    private _shouldRebuildOctree(
        ontologyId: string,
        projectId: string,
        opts: {
            ontologyBoundsContext?: IOntologyBoundsContext;
            rangeViewPosition?: IVector3D;
            distanceThreshold?: number;
            camPosition?: IVector3D;
            camDirection?: IVector3D;
            camYaw?: number;
            camPitch?: number;
            camFocalDistance?: number;
            repositionCamera?: boolean;
            assetSetIds?: string[];
            assetSetIsolation?: boolean;
        }
    ): boolean {

        const next: typeof this._lastOctreeRequest = {
            ontologyId,
            projectId,
            selectedOntologyId: opts.ontologyBoundsContext?.selectedOntologyId,
            mapBoundsSig: this._sigMapBounds(opts.ontologyBoundsContext),
            rangeViewPosition: opts.rangeViewPosition,
            distanceThreshold: opts.distanceThreshold,
            camPosition: opts.camPosition,
            camDirection: opts.camDirection,
            camYaw: opts.camYaw,
            camPitch: opts.camPitch,
            camFocalDistance: opts.camFocalDistance,
            repositionCamera: opts.repositionCamera,
            assetSetIds: (opts.assetSetIds ?? []).slice(0),
            assetSetIsolation: opts.assetSetIsolation
        };

        if (this._octreeRequestEquals(this._lastOctreeRequest, next)) {
            return false;
        }

        this._lastOctreeRequest = next;
        return true;
    }
}

export default Viewer3D;
